import { useNavigate, useParams } from "react-router";
import { CustomButtonModal } from "../../../../components/CustomButtonModal/CustomButtonModal";
import { CustomButtonTable } from "../../../../components/CustomButtonTable/CustomButtonTable";
import { CustomTypography } from "../../../../components/CustomTypography/CustomTypography";
import { IconWithBackground } from "../../../../components/IconWithBackground/IconWithBackground";
import { transformDataMileages } from "./data";
import useStyles from "./styles";
import { useTranslation } from "react-i18next";
import { useGetContratByVehicleId } from "../../../../api/contrat";
import { useGetOneVehicle, useSendMileageReminder } from "../../../../api/vehicule";
import { getCurrentFleetId } from "../../../../utils/getCurrentFleet";
import { getAssignedCollaborator } from "../../../../utils/assignedCollaborator";

type Props = {};

export const Mileage: React.FC<Props> = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const fleetId = getCurrentFleetId();
  const { id = "" } = useParams();
  const vehicleId = parseInt(id);
  const { contrats } = useGetContratByVehicleId(fleetId, vehicleId);
  const { vehicule } = useGetOneVehicle(fleetId, vehicleId);
  /*
   * Relance du RELEVÉ, pas des pièces du collaborateur.
   *
   * Ce bouton appelait useReminderDocumentMissing, c'est-à-dire l'endpoint qui
   * relance le permis et la carte d'identité : le conducteur recevait un mail
   * hors sujet, rien sur le kilométrage, et aucune notification dans l'app
   * mobile. Cette relance-là reste à sa place, sur la fiche collaborateur.
   *
   * Le ciblé est le véhicule, pas l'employé : c'est le backend qui résout
   * l'affectation active, avec la même règle que celle qui alimente cette
   * fiche. On évite ainsi de relancer un collaborateur que l'écran n'affiche
   * déjà plus.
   */
  const { mutate: sendReminder, isPending: isSendingReminder } =
    useSendMileageReminder(fleetId, vehicleId);

  /*
   * La garde lisait `vehicule?.userId`, un champ que le backend a déplacé dans
   * `assignedUser` : elle valait donc toujours `undefined` et le bouton était
   * désactivé en permanence, même sur un véhicule affecté. Voir
   * utils/assignedCollaborator.ts.
   */
  const { hasCollaborator, fullName } = getAssignedCollaborator(vehicule);
  const canRemind = hasCollaborator && !isSendingReminder;

  return (
    <div className={classes.container}>
      <div className={classes.containerHeader}>
        <CustomTypography
          isUppercase
          text={t("panel.mileage.mileage", "Mileage")}
          variant={"h5"}
          styles={{ containerText: classes.contratTitle }}
        />
        {/* <CustomIconButton
          iconName={"pencil"}
          widthIcon={"20px"}
          heightIcon={"20px"}
        /> */}
      </div>
      {transformDataMileages(contrats[0], vehicule).map((document, index) => (
        <div className={classes.subContainer} key={index}>
          <div className={classes.subContainer2}>
            <IconWithBackground
              iconName={document.icoName}
              iconColor={document.isAlert ? "#D93025" : undefined}
            />
            <div>
              <CustomTypography
                text={document.title}
                isUppercase
                variant="link"
                fontWeight={"bold"}
              />
              <CustomTypography
                text={document.subTitle}
                variant="link"
                {...(document.isAlert ? { color: "#D93025" } : {})}
              />
            </div>
          </div>
        </div>
      ))}
      <div className={classes.buttonContainer}>
        <CustomButtonTable
          // variant={"secondary"}
          variant={'default'}
          textVariant={"link"}
          label={t(
            "panel.mileage.relaunchTheEmployee",
            "Relaunch the employee"
          )}
          width={"fit-content"}
          fontWeight={"bold"}
          textDecoration={"none"}
          iconName={"bell"}
          padding={"0px 10px 0px 10px;"}
          disabled={!canRemind}
          /*
           * Un bouton désactivé sans explication est indiscernable d'un bouton
           * cassé — c'est exactement comme celui-ci a été signalé. Le title dit
           * POURQUOI on ne peut pas relancer, et qui sera relancé quand on peut.
           */
          title={
            hasCollaborator
              ? t("panel.mileage.remindAssigned", "Send a mileage reminder to {{name}}", {
                  name: fullName || t("panel.mileage.theDriver", "the assigned driver"),
                })
              : t(
                  "panel.mileage.noCollaboratorToRemind",
                  "No collaborator is assigned to this vehicle"
                )
          }
          onClick={() => sendReminder()}
        />
        <CustomButtonModal
          variant={"default"}
          textVariant={"link"}
          label={t("panel.mileage.seeOtherStatements", "See other statements")}
          width={"fit-content"}
          textDecoration={"none"}
          iconName={"chevronRight"}
          padding={"0px 10px 0px 10px;"}
          onClick={() => navigate(`/vehicule/${vehicleId}/history`)}
        />
      </div>
    </div>
  );
};
